import { EyeIcon, ShieldIcon } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { ACCESS_COPY } from '../accessCopy';
import { useWorkspace } from '../hooks/useWorkspaces';
import { useWorkspaceStanding } from '../hooks/useWorkspaceStanding';
import { RequestAccess } from './RequestAccess';
import { StandingDetails } from './StandingDetails';

/**
 * Where the reader stands, on the page itself (spec 196 US1).
 *
 * "View only" lived in the top bar alone, a small chip in a corner: a screenshot of
 * any workspace page could not tell a reader from a member. The band is the part of
 * the page that is read first, so it says it there, with the one thing to do about
 * it beside it. Drawn inside the band's `dark` scope, so the dark theme's measured
 * pairings are the band's own colours. Silent for a member, as the chip is.
 *
 * It is also the one place it is said: it opens what the standing means, and the
 * band showing it registers (`useRegisterBandStanding`); the top bar's chip shows
 * only while no band is registered, so a page never carries the same badge twice.
 */
export function StandingPill({ workspaceId }: { workspaceId: string }) {
  const standing = useWorkspaceStanding(workspaceId);
  const { data: workspace } = useWorkspace(workspaceId);
  const kind = standing.readOnly
    ? 'view-only'
    : standing.asOrgAdmin
      ? 'admin'
      : null;
  if (kind === null) return null;

  const name = workspace?.name ?? 'This workspace';
  const Icon = kind === 'view-only' ? EyeIcon : ShieldIcon;
  return (
    <div className="flex items-center gap-3">
      <Popover>
        <PopoverTrigger
          data-testid="standing-pill"
          aria-label={
            kind === 'view-only'
              ? ACCESS_COPY.viewOnlyLabel(name)
              : ACCESS_COPY.adminLabel(name)
          }
          className="border-border text-foreground hover:bg-accent focus-visible:ring-ring touch-target inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap transition-colors focus-visible:ring-3 focus-visible:outline-none"
        >
          <Icon aria-hidden="true" strokeWidth={1.5} className="size-3.5" />
          {kind === 'view-only'
            ? ACCESS_COPY.viewOnlyChip
            : ACCESS_COPY.adminChip}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-72 flex-col gap-1.5 p-4"
        >
          <StandingDetails kind={kind} workspaceName={name} />
        </PopoverContent>
      </Popover>
      {kind === 'view-only' ? (
        <RequestAccess workspaceId={workspaceId} size="sm" compact holdPlace />
      ) : null}
    </div>
  );
}
